import * as React from 'react';
import {Component} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import * as fieldTypes from '../../../../constants/FieldTypes';
import {selectField, activateField} from '../actions/Screen';
import {changeTitle, cancelTitleChange} from '../actions/Field';
import {fieldMaxLength} from '../../../../constants/FieldConsts';
import * as ItemTypes from '../../../../constants/ItemTypes';
import {withElementRef, InjectedElementRefProps} from '../../../components/Fields/withElementRef';
import {withScrollOnMove} from '../../../components/Fields/withScrollOnMove';
import {InjectedCollapsibleFieldProps} from '../../../../components/Common/Collapsible/Field';
import {InjectedParentFieldProps} from '../../../../components/Common/Collapsible/Collection';
import ToggleEditTitle from '../../../components/Fields/ToggleEditTitle';
import {isRTL} from '../../../../Localization';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';

interface Props {
    field: any;
}

@observer
class SystemReport extends Component<
    Props & InjectedElementRefProps & InjectedCollapsibleFieldProps & InjectedParentFieldProps,
    null
> {
    constructor(props) {
        super(props);
        this.activateField = this.activateField.bind(this);
        this.selectField = this.selectField.bind(this);
        this.changeTitle = this.changeTitle.bind(this);
    }

    activateField(e) {
        e.stopPropagation();
        const {field} = this.props;
        activateField(field);
    }

    selectField(e) {
        const {field, isParent, toggleExpanded} = this.props;
        selectField(field);

        if (isParent) {
            toggleExpanded();
        }
    }

    changeTitle(title, callback) {
        const {field} = this.props;
        changeTitle(field, title, callback);
    }

    renderItemImage() {
        const {field, isParent} = this.props;
        if (isParent) {
            const iconColor = field.isHiddenImage ? 'secondary-500' : 'primary-500';
            if (field.isDesign || field.repType === ItemTypes.Format) {
                return <Icon icon={PriorityIcon.section} iconColor={iconColor} />;
            } else {
                return <Icon icon={PriorityIcon.tableView} iconColor={iconColor} />;
            }
        } else {
            return null;
        }
    }

    renderMoreImage() {
        const {field} = this.props;

        return field.grouped ? null : (
            <Icon
                icon={PriorityIcon.threeDots}
                onClick={this.activateField}
                skin="primary"
                active={this.props.isExpanded}
            />
        );
    }

    renderHiddenImage() {
        const {field} = this.props;
        const {screen} = field;
        const fieldHiddenTooltip = screen.hiddenEntityName;

        return field.isHiddenImage ? (
            <ModernTooltip tooltip={fieldHiddenTooltip} position="top-end">
                <Icon icon={PriorityIcon.passwordHide} iconColor="secondary-400" />
            </ModernTooltip>
        ) : null;
    }

    render() {
        const {field, depth, isParent, isExpanded} = this.props;

        const {isHiddenImage, isChanging} = field;

        const fieldClass = classNames('field', {
            tab: field.type === fieldTypes.TAB,
            selected: field.isSelected,
            active: field.isActive && !isChanging,
            editInput: isChanging,
            expanded: isExpanded,
            hidden: isHiddenImage
        });

        const depthStyle = {
            marginRight: `${depth * 30}px`
        };

        const getExpandCollapseIcon = (): PriorityIcon => {
            if (isExpanded) {
                return PriorityIcon.arrowBottom;
            } else {
                if (isRTL()) {
                    return PriorityIcon.arrowLeft;
                } else {
                    return PriorityIcon.arrowRight;
                }
            }
        };

        return (
            <div className={fieldClass} onClick={this.selectField} ref={this.props.elementRef}>
                <div style={depthStyle} />
                {isParent && <Icon icon={getExpandCollapseIcon()} iconColor="primary-500" />}
                <ToggleEditTitle
                    isChanging={field.isChanging}
                    title={field.title}
                    maxLength={fieldMaxLength}
                    onApprove={this.changeTitle}
                    handleClose={() => cancelTitleChange(field)}
                    saveOnBlur
                >
                    {this.renderItemImage()}
                    <span className="report-text" title={field.title}>
                        {field.title}
                    </span>
                    <div className="images">
                        {this.renderHiddenImage()}
                        {this.renderMoreImage()}
                    </div>
                </ToggleEditTitle>
            </div>
        );
    }
}

const SystemReportFieldComponent = withElementRef(withScrollOnMove(SystemReport));
const SystemReportField = props => <SystemReportFieldComponent {...props} />;
export default SystemReportField;
